import {
  CustomLabel,
  FlexContainer,
  Input,
  Loader,
  SingleSelect,
  IOption,
  CollapsiblePanel,
  DateFormat,
  NumberFormat,
  CurrencyTypes,
  PhoneFormatTypes,
  Languages,
} from "@mindmemobile/mm-shared-global";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import Categories from "./Categories";
import {
  CountryWithStateModel,
  GeneralSettingsModel,
  TimeZoneModel,
} from "../../../shared/models";
import { DescriptionCell, LabelCell } from "../../../shared/styles";
import { CountryDropdown } from "../../../shared/components";

interface IProps {
  settings: GeneralSettingsModel;
  showLoader?: boolean;
  updateSettings: (settings: GeneralSettingsModel) => void;
}

type fieldValue =
  | CountryWithStateModel
  | CountryWithStateModel[]
  | string
  | boolean
  | number
  | DateFormat
  | PhoneFormatTypes
  | CurrencyTypes
  | TimeZoneModel;

const GeneralSettings: React.FC<IProps> = ({ settings, updateSettings }) => {
  const checkBoxInput = (
    key: keyof GeneralSettingsModel,
    value: boolean,
    label: string,
    disabled: boolean,
  ) => {
    return (
      <Input
        type="checkbox"
        value={value}
        onChange={(e) => handleInputChange(key, e.target.checked)}
        label={label}
        disabled={disabled || false}
      />
    );
  };

  const getEnumOptions = (enumObject: object): IOption[] => {
    return Object.entries(enumObject).map(([key, value]) => ({
      label: value,
      value: key,
    }));
  };

  const handleInputChange = (
    fieldName: keyof GeneralSettingsModel,
    value: fieldValue,
  ) => {
    const clonedData = new GeneralSettingsModel(settings);
    switch (fieldName) {
      case "countries":
        clonedData.countries = value as CountryWithStateModel[];
        break;
      case "country":
        clonedData.country = value as CountryWithStateModel;
        clonedData.filterTimeZones();
        break;
      case "timeZone":
        const selectedTimeZone = clonedData
          .getTimeZones()
          .find((tz) => tz.id === value);
        if (selectedTimeZone) {
          clonedData.timeZone = selectedTimeZone;
        }
        break;
      case "dateFormat":
        clonedData.dateFormat = value as keyof typeof DateFormat;
        break;
      case "numberFormat":
        clonedData.numberFormat = value as keyof typeof NumberFormat;
        break;
      case "currency":
        clonedData.currency = value as keyof typeof CurrencyTypes;
        break;
      case "phoneFormat":
        clonedData.phoneFormat = value as keyof typeof PhoneFormatTypes;
        break;
      case "language":
        clonedData.language = value as string;
        break;
      case "isUseDayLightSavings":
        clonedData.isUseDayLightSavings = value as boolean;
        break;
      case "includeCountry":
        clonedData.includeCountry = value as boolean;
        break;
      default:
        break;
    }
    updateSettings(clonedData);
  };

  const singleSelect = (
    selectedKey: string,
    fieldName: keyof GeneralSettingsModel,
    options: IOption[],
    hasError: boolean,
    className?: string,
  ) => {
    return (
      <SingleSelect
        hasError={hasError}
        selectedKey={selectedKey}
        nameKeys={["label"]}
        valueKeys={["value"]}
        options={[options]}
        onChange={(option: IOption) => {
          handleInputChange(fieldName, option.value);
        }}
        className={className}
      />
    );
  };

  const handleCountryChange = (
    id: string,
    field: keyof GeneralSettingsModel,
  ) => {
    if (id) {
      const country = settings.countries.find(
        (country: CountryWithStateModel) => country.id === id,
      );
      if (country) {
        handleInputChange(field, country);
      }
    }
  };

  const row = (label: string, children: React.ReactNode) => (
    <FlexContainer spacing={50} className="ps-general-sub-inputs">
      <LabelCell>
        <CustomLabel>{label}</CustomLabel>
      </LabelCell>
      <DescriptionCell>{children}</DescriptionCell>
    </FlexContainer>
  );

  function languageOptions(): IOption[] {
    return Object.keys(Languages).map((key) => {
      return {
        label: key,
        value: Languages[key as keyof typeof Languages],
      };
    });
  }

  return (
    <>
      <CollapsiblePanel
        title="General"
        className="payment-detail-header"
        collapsibleClassName="full-width"
        isOpened={true}
      >
        <FlexContainer
          fxLayout="column"
          spacing={15}
          className="ps-left-margin"
        >
          <FlexContainer spacing={50}>
            <LabelCell>
              <CustomLabel>Country</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              <CountryDropdown
                className="country-dropdown country-dropdown-expanded"
                selectedCountry={settings.country}
                // selectedCountry={new CountryWithStateModel()}
                error={settings.hasError("country")}
                errorMessage={"Please select country"}
                onCountryChange={(id: string) =>
                  handleCountryChange(id, "country")
                }
                availableCountries={settings.sortCountriesByName()}
              />
            </DescriptionCell>
          </FlexContainer>
          <FlexContainer spacing={50}>
            <LabelCell>
              <CustomLabel>Time Zone</CustomLabel>
            </LabelCell>
            <DescriptionCell>
              <FlexContainer alignItems="center" spacing={30}>
                {" "}
                {singleSelect(
                  settings.timeZone.id,
                  "timeZone",
                  settings
                    .getTimeZones()
                    .map((tz) => ({ label: tz.name, value: tz.id })),
                  settings.hasError("timeZone"),
                )}
                {(settings.country.abbreviationName === "US" ||
                  settings.country.abbreviationName === "CA") &&
                  checkBoxInput(
                    "isUseDayLightSavings",
                    settings.isUseDayLightSavings,
                    "Daylight Savings",
                    settings.hasError("isUseDayLightSavings"),
                  )}
              </FlexContainer>
            </DescriptionCell>
          </FlexContainer>
          {row(
            "Date Format",
            singleSelect(
              settings.dateFormat,
              "dateFormat",
              getEnumOptions(DateFormat),
              settings.hasError("dateFormat"),
            ),
          )}
          {row(
            "Currency Format",
            singleSelect(
              settings.numberFormat,
              "numberFormat",
              getEnumOptions(NumberFormat),
              settings.hasError("numberFormat"),
            ),
          )}
          {row(
            "Currency Symbol",
            singleSelect(
              settings.currency,
              "currency",
              getEnumOptions(CurrencyTypes),
              settings.hasError("currency"),
            ),
          )}
          {row(
            "Phone Format",
            <FlexContainer alignItems="center" spacing={20}>
              {singleSelect(
                settings.phoneFormat,
                "phoneFormat",
                getEnumOptions(PhoneFormatTypes),
                settings.hasError("phoneFormat"),
              )}
              {checkBoxInput(
                "includeCountry",
                settings.includeCountry,
                "Include Country",
                settings.hasError("includeCountry"),
              )}
            </FlexContainer>,
          )}
          {row(
            "Preferred Language",
            singleSelect(
              settings.language,
              "language",
              languageOptions(),
              settings.hasError("language"),
            ),
          )}
        </FlexContainer>
      </CollapsiblePanel>
    </>
  );
};

export default GeneralSettings;
